/**
 * ACI 申报凭证
 *
 * 设计来源：设计文档 §7.2.1（ACI）/ §7.3（区块：基本信息 / CCN / 参与方 / 汇总 / CONTAINERS）
 * - CONSOLIDATION = shipmentType（FCL/LCL/BBK 原样展示，D10）
 * - CCN 直接取提单号（D9），不截断
 * - CONTAINERS 无 SEAL NO 列（D4）；箱表无重量/体积字段（D8）
 *   故 Volume / Weight 取该箱匹配货行的本箱合计，QTY 取该箱匹配货行并列（不相加）
 */
import type { CertificateBuilder, CertificateContext, CertificateData, CertificateSection } from '../types'
import { registerCertificateBuilder, CERTIFICATE_TITLES } from './registry'
import {
  DASH,
  FieldCollector,
  billWithScac,
  cargoRowsForContainer,
  dedupeMissing,
  dictPort,
  findParty,
  formatDateTime,
  goodsDescription,
  gridSection,
  latestReceipt,
  logsSection,
  partyAddress,
  pick,
  qtyWithUnit,
  receiptValue,
  rowsOf,
  sumOf,
  tableSection,
  text,
} from './shared'

export const buildAciCertificate: CertificateBuilder = (
  context: CertificateContext
): CertificateData => {
  const detail = context.detail ?? {}
  const dict = context.dict
  const receipt = latestReceipt(context.receipts)
  const missing: string[] = []

  const info = new FieldCollector(missing)
  info
    .add('TYPE', pick(detail, 'entryType'))
    .add('CONSOLIDATION', pick(detail, 'shipmentType'))
    .add('TRANSMITTED(ET)', formatDateTime(pick(detail, 'sendTime') || pick(receipt, 'sendTime')))
    .add('PORT OF DISCHARGE', dictPort(dict, detail.dischargePortId))

  const ccn = new FieldCollector(missing)
  ccn
    .add('HOUSE BILL CCN', billWithScac(pick(detail, 'hblScac'), pick(detail, 'hblNo')))
    .add('PRIMARY CCN', billWithScac(pick(detail, 'mblScac'), pick(detail, 'mblNo')))

  const parties = new FieldCollector(missing)
  parties
    .add('SHIPPER', partyAddress(findParty(detail, 'SHIPPER'), { dict, contact: true }))
    .add('CONSIGNEE', partyAddress(findParty(detail, 'CONSIGNEE'), { dict, contact: true }))
    .add('NOTIFY', partyAddress(findParty(detail, 'NOTIFY'), { dict, contact: true }))
    .optional('DELIVERY ADDRESS', partyAddress(findParty(detail, 'DELIVERY_ADDRESS'), { dict, contact: true }))

  const cargoList = rowsOf(detail, 'cargoList')
  const totalFields = [
    { label: 'TOTAL VOLUME', value: sumOf(cargoList, 'volume'), unit: ' CBM' },
    { label: 'TOTAL WEIGHT', value: sumOf(cargoList, 'grossWeight'), unit: ' KGS' },
    { label: 'TOTAL QTY', value: sumOf(cargoList, 'quantity'), unit: '' },
  ].map(field => {
    if (!field.value) missing.push(field.label)
    return {
      label: field.label,
      value: field.value ? `${field.value}${field.unit}` : DASH,
    }
  })

  const containerRows = rowsOf(detail, 'containerList').map(row => {
    const cargo = cargoRowsForContainer(detail, text(row.containerNo))
    const volume = sumOf(cargo, 'volume')
    const weight = sumOf(cargo, 'grossWeight')
    return [
      text(row.containerNo) || DASH,
      volume ? `${volume} CBM` : DASH,
      weight ? `${weight} KGS` : DASH,
      qtyWithUnit(cargo, dict) || DASH,
      goodsDescription(cargo) || DASH,
    ]
  })

  const sections: CertificateSection[] = [
    gridSection(3, info.fields),
    gridSection(2, ccn.fields, 'CCN'),
    gridSection(2, parties.fields, 'PARTY INFORMATION'),
    gridSection(3, totalFields),
    tableSection(
      'CONTAINERS',
      ['Container NO.', 'Volume', 'Weight', 'QTY', 'GOODS DESCRIPTION'],
      containerRows,
      missing
    ),
    logsSection(context.receipts, missing),
  ]

  return {
    product: 'ACI',
    title: CERTIFICATE_TITLES.ACI,
    issuedAt: formatDateTime(
      receiptValue(context.receipts, 'receiptTime') ||
        pick(detail, 'receiptTime') ||
        pick(detail, 'dateModified')
    ),
    sections,
    missing: dedupeMissing(missing),
  }
}

registerCertificateBuilder('ACI', buildAciCertificate)
